import React, { useEffect, useState } from 'react';
import { workflowApi, sessionApi, fileApi, Script, ExecutionResult, FileUpload } from '../services/api';
import LoadingSpinner from './LoadingSpinner';

interface ExecutionLoaderProps {
  executionId: string;
  sessionId: string;
  onExecutionLoaded: (script: Script | null, executionResult: ExecutionResult | null, breakdown?: any, inputFiles?: FileUpload[], isAdaptedBreakdown?: boolean) => void;
  onError?: (error: string) => void;
}

const ExecutionLoader: React.FC<ExecutionLoaderProps> = ({ 
  executionId, 
  sessionId, 
  onExecutionLoaded,
  onError 
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadExecutionData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch execution details from the backend
        console.log('ExecutionLoader: Fetching execution details for:', executionId);
        const executionResponse = await workflowApi.getExecutionDetails(executionId);
        const executionData = executionResponse.data;
        console.log('ExecutionLoader: Received execution data:', executionData);

        if (!executionData || !executionData.execution) {
          throw new Error('Execution not found');
        }

        const execution = executionData.execution;
        const workflow = executionData.workflow;

        // Create a script object from the adapted script or original script
        const scriptCode = execution.adapted_script_code || workflow.original_script?.code || '';
        
        if (!scriptCode) {
          throw new Error('No script found for this execution');
        }

        const script: Script = {
          script_id: `execution-${executionId}`,
          session_id: sessionId,
          code: scriptCode,
          language: 'python',
          created_at: execution.created_at,
          executed: true
        };

        // Create execution result from the execution data
        console.log('ExecutionLoader: Creating execution result from:', execution);
        console.log('ExecutionLoader: results_summary:', execution.results_summary);
        console.log('ExecutionLoader: output_files:', execution.output_files);
        const executionResult: ExecutionResult = {
          script_id: script.script_id,
          success: execution.success || false,
          output: execution.results_summary?.output || '',
          error: execution.error || undefined,
          execution_time: execution.duration_seconds || 0,
          result_file_id: execution.results_summary?.result_file_id || execution.output_files?.[0]?.file_id || undefined,
          executed_at: execution.completed_at || execution.started_at
        };

        // Get the script breakdown - prefer adapted breakdown if available
        const breakdown = execution.adapted_script_breakdown || workflow.script_breakdown;
        const isAdaptedBreakdown = !!execution.adapted_script_breakdown;

        // Extract input files from execution and fetch their details
        const inputFileIds = execution.input_files || [];
        console.log('ExecutionLoader: Found input file IDs:', inputFileIds);
        
        let fileDetails: FileUpload[] = [];
        if (inputFileIds.length > 0) {
          try {
            // Fetch file details for each file ID
            const filePromises = inputFileIds.map(async (fileInfo: any) => {
              const fileId = typeof fileInfo === 'string' ? fileInfo : fileInfo.file_id;
              if (fileId) {
                try {
                  const response = await fileApi.getInfo(fileId);
                  return response.data;
                } catch (err) {
                  console.warn(`Failed to fetch file ${fileId}:`, err);
                  // Return a placeholder if file fetch fails
                  return {
                    file_id: fileId,
                    filename: fileInfo.role || 'Unknown File',
                    session_id: sessionId,
                    file_type: 'application/octet-stream',
                    size_bytes: 0,
                    upload_time: new Date().toISOString()
                  };
                }
              }
              return null;
            });
            
            const files = await Promise.all(filePromises);
            fileDetails = files.filter(f => f !== null) as FileUpload[];
            console.log('ExecutionLoader: Fetched file details:', fileDetails);
          } catch (err) {
            console.error('Failed to fetch file details:', err);
          }
        }

        // Call the callback with the loaded data
        console.log('ExecutionLoader: Calling onExecutionLoaded with:', { script, executionResult, breakdown, fileDetails, isAdaptedBreakdown });
        onExecutionLoaded(script, executionResult, breakdown, fileDetails, isAdaptedBreakdown);

      } catch (err: any) {
        console.error('Failed to load execution data:', err);
        const errorMessage = err.response?.data?.detail || err.message || 'Failed to load execution data';
        setError(errorMessage);
        if (onError) {
          onError(errorMessage);
        }
      } finally {
        setLoading(false);
      }
    };

    if (executionId && sessionId) {
      loadExecutionData();
    }
  }, [executionId, sessionId, onExecutionLoaded, onError]);

  // Optionally try to load latest execution for the session if no executionId
  useEffect(() => {
    const loadLatestExecution = async () => {
      if (!executionId && sessionId) {
        try {
          setLoading(true);
          const response = await sessionApi.getLatestExecution(sessionId);
          
          if (response.data.execution) {
            const execution = response.data.execution;
            
            // Create script from adapted script code
            if (execution.adapted_script_code) {
              const script: Script = {
                script_id: `execution-${execution.id}`,
                session_id: sessionId,
                code: execution.adapted_script_code,
                language: 'python',
                created_at: new Date().toISOString(),
                executed: true
              };

              const executionResult: ExecutionResult = {
                script_id: script.script_id,
                success: execution.success || false,
                output: '',
                error: undefined,
                execution_time: 0,
                result_file_id: execution.output_files?.[0]?.file_id || undefined,
                executed_at: execution.started_at || new Date().toISOString()
              };

              onExecutionLoaded(script, executionResult);
            }
          }
        } catch (err) {
          console.error('Failed to load latest execution:', err);
        } finally {
          setLoading(false);
        }
      }
    };

    if (!executionId && sessionId) {
      loadLatestExecution();
    }
  }, [executionId, sessionId, onExecutionLoaded]);

  if (loading) {
    return <LoadingSpinner message="Loading workflow execution..." />;
  }

  if (error) {
    return (
      <div className="execution-loader-error">
        <p>Failed to load execution data: {error}</p>
      </div>
    );
  }

  return null;
};

export default ExecutionLoader;